// @breda-spa shared,performance; mixed; see breda/projects/site-wide-spa/removal.md
// Static local catalog controls. The PHTML wrapper supplies translated labels and registers Alpine.
function createMurphyBeds(labels, PROMO_DISMISSED_KEY) {
    const filters = window.bredaMurphyFilters;
    const lifecycle = new AbortController();
    const listen = (target, name, fn, options = {}) => target.addEventListener(name, fn, {...options, signal: lifecycle.signal});
    const desktop = window.matchMedia('(min-width: 64rem)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // The component's state: plain variables, never reactive (no template binds them; apply() and the other
    // methods write the DOM themselves)
    let facts = null; // this document's public catalog facts
    let types = []; // the type filters (chip ids, in chip order): a bed must match every one
    let door = null; // the door filter or null
    let more = {}; // collection key => its extras shown (D23)
    let space = {}; // dimension (width, height, depth) => the visitor's limit in whole inches; absent without one
    let spaceOpen = false; // the space filter's panels are open
    let spaceFrame = 0;
    let spaceTimer = 0; // commits a slider's limits once it has settled
    let sheet = null; // the open sheet's name ('type' | 'size') or null
    let sheetOpener = null; // the button that opened it (focus returns there)
    let inert = []; // what openSheet() made inert
    let resizer = null; // the ResizeObserver of the sticky bars
    let spyLock = 0; // until when the scroll-spy leaves the tab a click chose (a timestamp)
    let spyFrame = 0;
    let why = null; // the greyed-out element whose reason the tooltip shows, null when it is closed
    let whyTimer = 0; // closes a tap's tooltip
    let whyFrame = 0;
    let pointer = 'mouse'; // the last press's pointer type: a tap's tooltip closes by itself
    let pressed = null; // the [data-mb-press] element a finger is on (data-pressed), null when none
    let pressTimer = 0;
    const fill = (template, ...values) => hyva.str(template, ...values);
    const beds = (count) => fill(count === 1 ? labels.bed : labels.beds, count);
    const bedsShort = (count) => fill(count === 1 ? labels.count1 : labels.count, count);
    const has = (object, key) => key !== null && Object.prototype.hasOwnProperty.call(object, key);
    // type ids in chip order (which also drops a repeat), without one, with one more
    const typeOrder = (ids) => Object.keys(facts.filters.types).filter((id) => ids.includes(id));
    const without = (ids, id) => ids.filter((other) => other !== id);
    const typesWith = (id) => typeOrder(types.concat(id));
    const joinNames = (ids) => ids.map((id) => facts.filters.copy[id].label)
        .reduce((all, name) => fill(labels.both, all, name));
    const inches = (value) => value + '″';
    // a length custom property of <html> in px (--breda-header-h, --mb-nav-h, --mb-bar-h); 0 when unset
    const cssPx = (name) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
    const dimensions = () => facts.filters.space.dims;
    const limited = (limits = space) => Object.keys(dimensions()).filter((key) => has(limits, key));
    // the active filters as one ("Sofa", "Sofa + Slab doors + up to 80″ wide"), '' without one; except leaves
    // chips out (a type chip's own id; the active door for a door chip), withSpace false the limits
    const filterNames = (except = [], withSpace = true) => {
        const chips = types.concat(door ? [door] : []).filter((id) => !except.includes(id))
            .map((id) => facts.filters.copy[id].label);
        const limits = withSpace ? limited().map((key) => fill(dimensions()[key].limit, inches(space[key]))) : [];
        const names = [...chips, ...limits];

        return names.length ? names.reduce((all, name) => fill(labels.both, all, name)) : '';
    };
    // the limits with one dimension set to a slider's value: none at the slider's maximum
    const limitsWith = (limits, key, value) => {
        const next = Object.assign({}, limits);
        value >= dimensions()[key].max ? delete next[key] : (next[key] = value);

        return next;
    };

    return {
        init() {
            const region = this.region();
            if (!region || !this.readFacts(region)) {
                return;
            }
            const here = new URL(location.href);
            // @breda-spa shared: Safari can traverse history before deferred catalog startup.
            // Do not seed this catalog's URL over the Back destination. Swup owns later visits.
            if (!window.bredaNavigation && here.pathname.replace(/\/$/, '')
                !== new URL(facts.page.key, location.href).pathname.replace(/\/$/, '')) {
                location.reload();
                return;
            }
            if ('scrollRestoration' in history && !window.bredaNavigation) {
                history.scrollRestoration = 'auto';
            }
            const html = document.documentElement;
            // sticky bars never cover a focused element or an anchor's target (§11); the fixed phone bar never
            // covers the footer: its height (--mb-bar-h), the 12px it floats above the bottom edge and 12px of air
            html.classList.add('max-lg:scroll-pb-[calc(var(--mb-bar-h,0px)+24px+env(safe-area-inset-bottom))]');
            document.body.classList.add('max-lg:pb-[calc(var(--mb-bar-h,0px)+24px+env(safe-area-inset-bottom))]');

            const initialFilters = this.filtersOf(here);
            const state = this.ownState(history.state);
            types = initialFilters.types;
            door = initialFilters.door;
            space = initialFilters.space;
            more = state ? Object.assign({}, state.more) : {};
            this.setUp();
            filters.handoff();
            // The browser positions fragment targets and restores scroll; no delayed correction.
            // seed the entry (a direct load, a filter and Back would otherwise return to an entry without state);
            // the address takes the server's page key (data-page: a ?size= it did not accept leaves, so keyOf()
            // of the address is the page's key) and a filter the page cannot offer leaves too (D41)
            const seed = new URL(facts.page.key, location.href);
            here.searchParams.forEach((value, name) => name === 'size' || seed.searchParams.append(name, value));
            this.record(false, this.compose(seed, true) + here.hash);

            const root = this.$root;
            listen(root, 'click', (event) => this.onClick(event));
            listen(root, 'keydown', (event) => this.onKeydown(event));
            listen(root, 'input', (event) => this.onSpaceInput(event));
            listen(root, 'change', (event) => this.onSpaceChange(event));
            listen(root, 'pointerdown', (event) => this.onPointerDown(event));
            ['pointerover', 'pointerout', 'focusin', 'focusout'].forEach((name) => {
                listen(root, name, (event) => this.onWhy(event), {passive: true});
            });
            ['touchstart', 'touchend', 'touchcancel'].forEach((name) => {
                listen(root, name, (event) => this.onPress(event), {passive: true});
            });
            // the tooltip follows its element through any scroll (the page, a sheet, a strip), a resize and a
            // transition; a press elsewhere closes it
            const follow = () => {
                if (why && !whyFrame) {
                    whyFrame = window.requestAnimationFrame(() => {
                        whyFrame = 0;
                        this.placeWhy();
                    });
                }
            };
            ['scroll', 'transitionend'].forEach((name) => {
                listen(document, name, follow, {capture: true, passive: true});
            });
            listen(window, 'resize', follow, {passive: true});
            listen(document, 'pointerdown', (event) => {
                pointer = event.pointerType;
                why && !why.contains(event.target) && this.hideWhy();
            }, {capture: true, passive: true});
            listen(window, 'popstate', (event) => this.onPopState(event));
            listen(window, 'hashchange', () => this.reveal(location.hash));
            listen(window, 'scroll', () => {
                spyFrame = spyFrame || window.requestAnimationFrame(() => this.spy());
            }, {passive: true});
            ['scrollend', 'wheel', 'touchmove'].forEach((name) => {
                listen(window, name, () => { spyLock = 0; }, {passive: true});
            });
            listen(document, 'keydown', (event) => {
                if (event.key !== 'Escape') {
                    return;
                }
                this.hideWhy();
                if (sheet) {
                    this.closeSheet(true);
                }
            });
            listen(desktop, 'change', () => this.closeSheet(false));
            listen(window, 'pagehide', () => this.snapshot());
            listen(window, 'breda:navigation:before', () => { this.snapshot(); this.closeSheet(false); });
            listen(window, 'pageshow', (event) => {
                if (event.persisted) {
                    this.closeSheet(false);
                    this.spy();
                }
            });
            listen(document, 'visibilitychange', () => {
                document.visibilityState === 'hidden' && this.snapshot();
            });

            // Alpine removes cloaks in microtasks. Batch the local measurements in the next frame,
            // before paint, rather than making a second round of unchanged DOM writes after paint.
            window.requestAnimationFrame(() => {
                if (lifecycle.signal.aborted || !region.isConnected) return;
                region.querySelectorAll('[data-mb-strip] [aria-current]').forEach((element) => this.centre(element));
                // Only combined labels need their visible width checked again after uncloaking.
                if (types.length + (door ? 1 : 0) > 1) {
                    this.applyBar(region, true, this.count());
                }
                this.spy();
                window.dispatchEvent(new CustomEvent('bredabeds:murphy-beds-ready'));
            });
        },

        destroy() {
            lifecycle.abort();
            resizer?.disconnect();
            this.closeSheet(false);
            [spaceTimer, whyTimer, pressTimer].forEach(clearTimeout);
            [spaceFrame, spyFrame, whyFrame].forEach(cancelAnimationFrame);
            document.documentElement.classList.remove('max-lg:scroll-pb-[calc(var(--mb-bar-h,0px)+24px+env(safe-area-inset-bottom))]');
            document.body.classList.remove('max-lg:pb-[calc(var(--mb-bar-h,0px)+24px+env(safe-area-inset-bottom))]');
        },

        // ---- the region and its facts ----------------------------------------------------------------------

        region() {
            return document.getElementById('murphy-beds-page');
        },

        readFacts(region) {
            try {
                facts = JSON.parse(region.querySelector('#murphy-beds-data').textContent);
                filters.project(facts, filters.parse(facts, new URL(location.href)));
                return true;
            } catch (error) {
                return false;
            }
        },

        /** Initialize this document’s local controls. */
        setUp() {
            const region = this.region();
            this.setUpPromo();
            this.setUpFaq();
            this.observeBars();
            // The initial frame measures links once after Alpine removes cloaks.
            this.apply(false);
        },

        /** The page key of a URL: its path, plus ?size= on a collection page (data-page of its region). */
        keyOf(url) {
            const size = url.searchParams.get('size');
            const collection = facts.collection_paths.includes(url.pathname);

            return collection && size ? url.pathname + '?size=' + size : url.pathname;
        },

        /**
         * The types (?type=storage,horizontal: in chip order, each only if the page offers it, D41) and the door
         * named by a URL, and its space limits: a whole number of inches each, kept as it is (a limit is the
         * visitor's room, whatever the page's range)
         */
        filtersOf(url) {
            return filters.parse(facts, url);
        },

        /**
         * A URL with the component's filters, query in the order size, type (its ids joined with plain commas: a
         * query needs none escaped), door, the limits (others kept only for the current address); path and query only.
         */
        compose(url, keepOthers, withTypes = types, withDoor = door, withSpace = space) {
            const target = new URL(url, location.href);
            const query = new URLSearchParams();
            const size = target.searchParams.get('size');
            size && query.set('size', size);
            withTypes.length && query.set('type', withTypes.join(','));
            withDoor && query.set('door', withDoor);
            limited(withSpace).forEach((key) => query.set(key, String(withSpace[key])));
            if (keepOthers) {
                const own = ['size', 'type', 'door', ...Object.keys(dimensions())];
                target.searchParams.forEach((value, name) => {
                    own.includes(name) || query.append(name, value);
                });
            }
            const search = query.toString().replace(/%2C/gi, ',');

            return target.pathname + (search ? '?' + search : '');
        },

        ownState(state) {
            return filters.ownState(facts, state);
        },

        snapshot() {
            if (this.keyOf(new URL(location.href)) === facts.page.key) this.record(false);
        },

        record(push, url) {
            const state = Object.assign({}, history.state, {
                murphyBeds: {version: 2, key: facts.page.key, more: Object.assign({}, more)}
            });
            bredaPage.history(push, url, state);
        },

        // ---- filters and extras ----------------------------------------------------------------------------

        /** Whether a bed matches the types (every one), the door and the limits */
        matcher(withTypes = types, withDoor = door) {
            return filters.matcher(facts, {types: withTypes, door: withDoor, space});
        },

        fitter(size, limits = space) {
            return filters.fitter(facts, size, limits);
        },

        /** The beds a size link's page shows under the filters and the limits (filters.sizes, one size's) */
        sizeCount(scope, size) {
            const sets = types.map((id) => new Set(scope.types[id] || []));
            const doors = door ? new Set(scope.doors[door] || []) : null;
            const fits = this.fitter(size);

            return scope.beds.filter((id) => sets.every((set) => set.has(id)) && (!doors || doors.has(id)) && fits(id)).length;
        },

        count() {
            return facts.beds.filter(this.matcher()).length;
        },

        /**
         * The results heading: one type's heading, or several types' labels as one ("Storage + Horizontal Murphy
         * Beds", "Horizontal Murphy Beds under $1,500"; filters.combined); with a door, its phrase after that;
         * the door's heading alone; the space filter's line; '' unfiltered
         */
        /** Shows the state: tiles, sections, extras toggles, headings, counts, chips, tabs, the bar, the links. */
        apply(measureLinks = true) {
            this.hideWhy();
            const region = this.region();
            const matches = this.matcher();
            const filtered = Boolean(types.length || door || limited().length);
            const count = this.count();
            filters.paint(region, filters.project(facts, {types, door, space}, more));

            // no bed left: the empty state names the filters and offers a Clear for each one set
            const empty = region.querySelector('[data-mb-empty]');
            if (empty) {
                empty.hidden = !filtered || count > 0;
                empty.querySelector('[data-mb-empty-title]').textContent = empty.hidden ? ''
                    : fill(labels.empty, filterNames());
                empty.querySelectorAll('[data-mb-empty-clear]').forEach((button) => {
                    const group = button.dataset.mbEmptyClear;
                    if (group === 'type' || group === 'door') {
                        const ids = group === 'type' ? types : (door ? [door] : []);
                        button.hidden = ids.length === 0;
                        button.textContent = ids.length ? fill(labels.clear, joinNames(ids)) : '';
                    } else if (group === 'space') {
                        button.hidden = limited().length === 0;
                        button.textContent = labels.resetSpace;
                    }
                });
            }

            const none = region.dataset.mbNone || '';
            const noneSize = region.dataset.mbNoneSize || '';
            region.querySelectorAll('a[data-mb-chip]').forEach((chip) => {
                const group = chip.dataset.mbGroup;
                const id = chip.dataset.mbChip;
                const active = group === 'type' ? types.includes(id) : door === id;
                const members = facts.filters[group === 'type' ? 'types' : 'doors'][id] || [];
                // the beds the chip would leave: its own with the other filters (the other types, the door, the limits)
                const others = group === 'type' ? this.matcher(without(types, id), door) : this.matcher(types, null);
                const number = members.filter(others).length;
                // the counter's format is the markup's: data-mb-chip-count="beds" ("14 beds") or "number" ("14")
                const counter = chip.querySelector('[data-mb-chip-count]');
                if (counter) {
                    counter.textContent = counter.dataset.mbChipCount === 'beds' ? bedsShort(number) : String(number);
                }
                this.mark(chip, active ? 'true' : null);
                // without a bed on the page, or none left by the other filters and the limits
                const blocking = filterNames(group === 'type' ? [id] : (door ? [door] : []));
                this.disable(chip, number === 0 && !active, members.length === 0 || !blocking ? none
                    : fill(labels.noneWith, blocking));
                chip.setAttribute('href', this.compose(facts.page.key, false,
                    group === 'type' ? (active ? without(types, id) : typesWith(id)) : types,
                    group === 'door' ? (active ? null : id) : door));
            });
            region.querySelectorAll('a[data-mb-clear]').forEach((clear) => {
                const group = clear.dataset.mbClear;
                clear.hidden = group === 'type' ? types.length === 0 : !door;
                clear.setAttribute('href', this.compose(facts.page.key, false,
                    group === 'type' ? [] : types, group === 'door' ? null : door));
            });
            region.querySelectorAll('a[data-mb-size], a[data-mb-tab]').forEach((link) => {
                link.setAttribute('href', this.compose(link.getAttribute('href'), false));
            });
            // a size link: the beds its page shows with these filters and limits, disabled at none
            region.querySelectorAll('a[data-mb-size]').forEach((link) => {
                const scope = facts.filters.sizes[link.dataset.mbSize || 'all'];
                const number = scope ? this.sizeCount(scope, link.dataset.mbSize || null) : 0;
                const counter = link.querySelector('[data-mb-size-count]');
                if (counter) {
                    counter.textContent = bedsShort(number);
                }
                this.disable(link, number === 0 && !link.hasAttribute('aria-current'),
                    scope ? fill(labels.noneWith, filterNames()) : noneSize);
            });
            // the empty state's sizes: those with a match, never the page's own
            const sizes = region.querySelector('[data-mb-empty-sizes]');
            if (sizes) {
                let offered = 0;
                sizes.querySelectorAll('a[data-mb-size]').forEach((link) => {
                    const off = link.getAttribute('aria-disabled') === 'true'
                        || (link.dataset.mbSize || null) === facts.page.size;
                    link.closest('li').hidden = off;
                    offered += off ? 0 : 1;
                });
                sizes.hidden = offered === 0;
            }
            if (facts.page.kind !== 'collection') {
                region.querySelectorAll('a[data-mb-tab]').forEach((tab) => {
                    const section = document.getElementById('col-' + tab.dataset.mbTab);
                    this.disable(tab, Boolean(section && section.hidden), fill(labels.noneWith, filterNames()));
                });
            }

            // the cabinet table: the rows of the beds shown, the columns the limits emphasise; hidden with no row
            const table = region.querySelector('[data-mb-dimensions-table]');
            if (table) {
                let rows = 0;
                table.querySelectorAll('tr[data-mb-bed]').forEach((row) => {
                    row.hidden = filtered && !matches(Number(row.dataset.mbBed));
                    rows += row.hidden ? 0 : 1;
                });
                table.closest('[data-mb-dimensions]').hidden = rows === 0;
                Object.keys(dimensions()).forEach((key) => table.toggleAttribute('data-limit-' + key, has(space, key)));
            }

            this.applySpace(region, count);
            this.applyBar(region, filtered, count);
            if (measureLinks) this.updateNavigationLinks();
        },

        /** The space filter's every copy: sliders, values, toggles and their summary, panels, Reset, the count */
        applySpace(region, count) {
            const dims = dimensions();
            const set = limited();
            region.querySelectorAll('input[data-mb-space]').forEach((input) => {
                const key = input.dataset.mbSpace;
                const dimension = dims[key];
                // a limit outside the page's range keeps its words; the thumb stops at the track's end
                const limit = has(space, key) ? space[key] : null;
                const position = Math.min(Math.max(limit ?? dimension.max, dimension.min), dimension.max);
                const share = (position - dimension.min) / Math.max(1, dimension.max - dimension.min);
                input.value = String(position);
                input.toggleAttribute('data-active', limit !== null);
                input.style.setProperty('--mb-fill', share * 100 + '%');
                input.setAttribute('aria-valuetext', limit !== null ? fill(dimension.limit, inches(limit)) : dimension.any);
            });
            region.querySelectorAll('[data-mb-space-value]').forEach((element) => {
                const key = element.dataset.mbSpaceValue;
                element.textContent = has(space, key) ? fill(labels.upTo, inches(space[key])) : labels.any;
            });
            region.querySelectorAll('[data-mb-space-toggle]').forEach((toggle) => {
                toggle.setAttribute('aria-expanded', String(spaceOpen));
                toggle.toggleAttribute('data-active', set.length > 0);
            });
            region.querySelectorAll('[data-mb-space-summary]').forEach((summary) => {
                summary.textContent = set.length
                    ? set.map((key) => fill(dims[key].short, inches(space[key]))).join(' · ') : summary.dataset.mbHint;
            });
            region.querySelectorAll('[data-mb-space-panel]').forEach((panel) => { panel.hidden = !spaceOpen; });
            region.querySelectorAll('[data-mb-space-reset], [data-mb-space-dot]').forEach((element) => {
                element.hidden = set.length === 0;
            });
            region.querySelectorAll('[data-mb-space-count]').forEach((element) => {
                element.textContent = set.length ? fill(labels.fit, bedsShort(count)) : '';
            });
        },

        applyBar(region, filtered, count) {
            const typeLabel = region.querySelector('[data-mb-bar-type]');
            if (typeLabel) {
                // the filters' names; when they overflow the button, how many there are ("3 filters")
                const active = types.length + (door ? 1 : 0);
                typeLabel.textContent = filterNames([], false) || labels.allTypes;
                if (active > 1 && typeLabel.scrollWidth > typeLabel.clientWidth) {
                    typeLabel.textContent = fill(labels.filters, active);
                }
            }
            // the type sheet's Done shows what the filters leave ("Show 14 beds") while a type or door is set
            region.querySelectorAll('[data-mb-sheet-done]').forEach((done) => {
                done.textContent = types.length || door ? fill(labels.show, bedsShort(count)) : labels.done;
            });
            const barCount = region.querySelector('[data-mb-bar-count]');
            if (barCount) {
                barCount.textContent = String(count);
            }
            const opener = region.querySelector('[data-mb-open="type"]');
            if (opener) {
                opener.classList.toggle('bg-stone-800', Boolean(types.length || door));
            }
            const clearAll = region.querySelector('[data-mb-clear-all]');
            if (clearAll) {
                clearAll.hidden = !filtered || Boolean(sheet);
            }
            // the active filter's photo on the "Shop by" button (the first type's, else the door's, else the
            // default): one of the photos the bar prints, [data-mb-bar-photo="<chip id>"] ('' for the default)
            const image = region.querySelector('[data-mb-bar-image]');
            if (image) {
                const photos = Array.from(image.querySelectorAll('[data-mb-bar-photo]'));
                const photo = (id) => photos.find((element) => element.dataset.mbBarPhoto === id);
                const shown = photo(types[0] || door || '') || photo('');
                photos.forEach((element) => { element.hidden = element !== shown; });
            }
        },

        mark(element, value) {
            if (element.getAttribute('aria-current') === value) return;
            value === null ? element.removeAttribute('aria-current') : element.setAttribute('aria-current', value);
        },

        /** aria-disabled with its reason (aria-description, the tooltip's text); it stays focusable (§11) */
        disable(element, disabled, reason) {
            if (disabled) {
                element.setAttribute('aria-disabled', 'true');
                element.setAttribute('aria-description', reason);
            } else {
                element.removeAttribute('aria-disabled');
                element.removeAttribute('aria-description');
            }
        },

        /**
         * Mouse hover and keyboard focus show a greyed-out element's reason. Focus closes it when it leaves the
         * element; the pointer when it leaves both the element and the tooltip, after a moment that lets it cross
         * the gap between them.
         */
        onWhy(event) {
            const closest = (selector) => event.target.closest && event.target.closest(selector);
            const target = closest('[aria-disabled="true"][aria-description]');
            if (event.type === 'focusin') {
                target && target.matches(':focus-visible') && this.showWhy(target, false);
            } else if (event.type === 'focusout') {
                why && target === why && this.hideWhy();
            } else if (event.pointerType !== 'mouse') {
                // a tap's tooltip comes with its click
            } else if (event.type === 'pointerover') {
                if (target) {
                    this.showWhy(target, false);
                } else if (why && closest('[data-mb-why-tip]')) {
                    window.clearTimeout(whyTimer);
                }
            } else if (why && (target === why || closest('[data-mb-why-tip]'))) {
                const next = event.relatedTarget;
                if (!next || !(why.contains(next) || (next.closest && next.closest('[data-mb-why-tip]')))) {
                    window.clearTimeout(whyTimer);
                    whyTimer = window.setTimeout(() => this.hideWhy(), 300);
                }
            }
        },

        /** The tooltip with the element's reason; a tap's closes by itself. */
        showWhy(element, tapped) {
            const tip = this.$root.querySelector('[data-mb-why-tip]');
            const reason = element.getAttribute('aria-description');
            if (!tip || !reason) {
                return;
            }
            window.clearTimeout(whyTimer);
            why = element;
            tip.querySelector('[data-mb-why-text]').textContent = reason;
            this.placeWhy();
            if (tapped) {
                whyTimer = window.setTimeout(() => this.hideWhy(), 3000);
            }
        },

        /**
         * Places the tooltip for its element where it is now: centred above it (below it without room above),
         * inside the viewport. An element no longer shown (a closed sheet) closes it.
         */
        placeWhy() {
            const tip = this.$root.querySelector('[data-mb-why-tip]');
            const box = why && why.isConnected ? why.getBoundingClientRect() : null;
            if (!tip || !box || (box.width === 0 && box.height === 0)) {
                this.hideWhy();
                return;
            }
            const gap = 10;
            const edge = 8;
            const above = box.top - tip.offsetHeight - gap >= edge;
            const centre = box.left + box.width / 2;
            const left = Math.max(edge,
                Math.min(centre - tip.offsetWidth / 2, window.innerWidth - tip.offsetWidth - edge));
            const top = above ? box.top - tip.offsetHeight - gap : box.bottom + gap;
            tip.dataset.side = above ? 'above' : 'below';
            tip.style.transform = 'translate(' + Math.round(left) + 'px, ' + Math.round(top) + 'px)';
            tip.querySelector('[data-mb-why-arrow]').style.left
                = Math.round(Math.max(12, Math.min(centre - left, tip.offsetWidth - 12))) + 'px';
            tip.setAttribute('data-show', '');
        },

        hideWhy() {
            window.clearTimeout(whyTimer);
            if (!why) {
                return;
            }
            why = null;
            const tip = this.$root.querySelector('[data-mb-why-tip]');
            tip && tip.removeAttribute('data-show');
        },

        /**
         * A visitor's filter click or slider release: apply in place, a new history entry, the announcement, a Tags
         * event per type chip set (its id) or unset (''), per door change and per dimension changed; a chip or a
         * slider in a sheet leaves it open (keepSheet); a Clear closes it.
         */
        setFilters(nextTypes, nextDoor, nextSpace = space, keepSheet = false) {
            const added = nextTypes.filter((id) => !types.includes(id));
            const removed = types.filter((id) => !nextTypes.includes(id));
            const changedDoor = nextDoor !== door;
            const resized = Object.keys(dimensions()).filter((key) => space[key] !== nextSpace[key]);
            if (added.length === 0 && removed.length === 0 && !changedDoor && resized.length === 0) {
                this.apply();
                return;
            }
            this.snapshot();
            types = typeOrder(nextTypes);
            door = nextDoor;
            space = Object.assign({}, nextSpace);
            // Save the old entry's native scroll position before filtering changes its layout.
            this.record(true, this.compose(location.href, true));
            this.apply();
            keepSheet || this.closeSheet(true);
            this.keepResultsInView();
            this.spy();
            const count = this.count();
            this.announce(beds(count));
            const changes = added.map((id) => ['type', id])
                .concat(removed.length ? [['type', '']] : [], changedDoor ? [['door', door || '']] : []);
            changes.forEach(([group, value]) => {
                window.dispatchEvent(new CustomEvent('bredabeds:filter-change',
                    {detail: {group: group, value: value, count: count}}));
            });
            resized.forEach((key) => {
                window.dispatchEvent(new CustomEvent('bredabeds:filter-change',
                    {detail: {group: key, value: has(space, key) ? String(space[key]) : '', count: count}}));
            });
        },

        /** A slider moving: the limits follow it live (one apply per frame); onSpaceChange records them */
        onSpaceInput(event) {
            const input = event.target.closest && event.target.closest('input[data-mb-space]');
            if (!input) {
                return;
            }
            space = limitsWith(space, input.dataset.mbSpace, Number(input.value));
            spaceFrame = spaceFrame || window.requestAnimationFrame(() => {
                spaceFrame = 0;
                this.apply();
            });
        },

        /** A slider released or stepped by a key: the limits are recorded once it has settled (commitSpace) */
        onSpaceChange(event) {
            if (!event.target.closest || !event.target.closest('input[data-mb-space]')) {
                return;
            }
            window.clearTimeout(spaceTimer);
            spaceTimer = window.setTimeout(() => this.commitSpace(), 500);
        },

        /**
         * The live limits as a filter change from those the address records (the committed ones): one history
         * entry and one Tags event for a whole drag or run of key steps.
         */
        commitSpace() {
            const next = space;
            space = this.filtersOf(new URL(location.href)).space;
            window.cancelAnimationFrame(spaceFrame);
            spaceFrame = 0;
            this.setFilters(types, door, next, true);
        },

        /**
         * Opens or closes every copy of the space filter; an opened panel inside a scrolling box (a sheet, the
         * popover, the desktop column) scrolls into it, so its sliders are on screen however short the screen
         */
        toggleSpace(toggle) {
            spaceOpen = !spaceOpen;
            this.applySpace(this.region(), this.count());
            const panel = spaceOpen && document.getElementById(toggle.getAttribute('aria-controls'));
            if (panel && panel.closest('[data-mb-sheet], [data-slot="column"]')) {
                panel.scrollIntoView({block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth'});
            }
        },

        /** A type chip sets or unsets itself among the types; a door chip is the door, or unsets it */
        pickChip(chip) {
            const group = chip.dataset.mbGroup;
            const id = chip.dataset.mbChip;
            if (group === 'type') {
                this.setFilters(types.includes(id) ? without(types, id) : typesWith(id), door, space, true);
            } else {
                this.setFilters(types, door === id ? null : id, space, true);
            }
        },

        /** An extras toggle records local expansion for history. */
        toggleMore(toggle) {
            const key = toggle.dataset.mbMore;
            more[key] = !more[key];
            this.apply();
            this.record(false);
        },

        /**
         * A Clear control that held focus and is now hidden (no filter left) hands focus on (§11): a card's Clear to
         * its first chip, the bar's "Clear filters" to "Shop by", else the H1.
         */
        focusAfterClear(control) {
            if (!control.hidden) {
                return;
            }
            const card = 'mbClear' in control.dataset ? control.closest('nav') : null;
            const next = card ? card.querySelector('a[data-mb-chip]')
                : this.region().querySelector('[data-mb-open="type"]');
            next ? next.focus({preventScroll: true}) : this.focusHeading();
        },

        announce(text) {
            const live = this.$root.querySelector('[data-mb-live]');
            if (live) {
                live.textContent = '';
                window.setTimeout(() => { live.textContent = text; }, 100);
            }
        },

        // ---- clicks, keys, intent ----------------------------------------------------------------------------

        onClick(event) {
            if (event.defaultPrevented) {
                return;
            }
            const backdrop = event.target.closest('[data-mb-backdrop]');
            if (backdrop) {
                this.closeSheet(true);
                return;
            }
            const control = event.target.closest('a, button');
            if (!control || !this.$root.contains(control)) {
                return;
            }
            const data = control.dataset;
            const link = control.tagName === 'A';
            if (link && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
                return;
            }
            // a greyed-out chip, size or tab does nothing but say why (a tap has no hover to show it); a click
            // from the keyboard has no press (detail 0)
            if (control.getAttribute('aria-disabled') === 'true') {
                event.preventDefault();
                this.showWhy(control, event.detail > 0 && pointer !== 'mouse');
                return;
            }
            if ('mbChip' in data) {
                event.preventDefault();
                this.pickChip(control);
            } else if ('mbClear' in data) {
                event.preventDefault();
                const focused = control === document.activeElement;
                this.setFilters(data.mbClear === 'type' ? [] : types, data.mbClear === 'door' ? null : door);
                focused && this.focusAfterClear(control);
            } else if ('mbClearAll' in data) {
                const focused = control === document.activeElement;
                this.setFilters([], null, {});
                focused && this.focusAfterClear(control);
            } else if ('mbSpaceToggle' in data) {
                this.toggleSpace(control);
            } else if ('mbEmptyClear' in data) {
                const group = data.mbEmptyClear;
                const all = group === 'all';
                this.setFilters(all || group === 'type' ? [] : types, all || group === 'door' ? null : door,
                    all || group === 'space' ? {} : space);
                this.focusResults();
            } else if ('mbSkip' in data) {
                event.preventDefault();
                data.mbSkip === 'filters' ? this.focusFilters() : this.focusResults();
            } else if ('mbSpaceReset' in data) {
                const focused = control === document.activeElement;
                this.setFilters(types, door, {}, true);
                // Reset hides itself: focus moves to its panel's first slider
                focused && control.closest('[data-mb-space-panel]').querySelector('input[data-mb-space]').focus();
            } else if ('mbSize' in data) {
                if (this.keyOf(new URL(control.href)) === facts.page.key) {
                    event.preventDefault();
                    this.closeSheet(true);
                } else {
                    this.updateNavigationLinks();
                    this.closeSheet(false);
                }
            } else if ('mbTab' in data && facts.page.kind !== 'collection') {
                event.preventDefault();
                this.scrollToSection(control);
            } else if ('mbMore' in data) {
                this.toggleMore(control);
            } else if ('mbOpen' in data) {
                this.openSheet(data.mbOpen, control);
            } else if ('mbClose' in data) {
                this.closeSheet(true);
            } else if ('mbFaqTab' in data) {
                this.selectFaq(data.mbFaqTab, false);
            } else if ('promoDismiss' in data) {
                this.dismissPromo(control);
            }
        },

        onKeydown(event) {
            if (event.key === 'Tab' && sheet) {
                this.trapTab(event);
                return;
            }
            const tab = event.target.closest('[data-mb-faq-tab]');
            if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                return;
            }
            const tabs = Array.from(tab.parentElement.querySelectorAll('[data-mb-faq-tab]'));
            const index = tabs.indexOf(tab);
            const next = {ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: tabs.length - 1}[event.key];
            event.preventDefault();
            this.selectFaq(tabs[(next + tabs.length) % tabs.length].dataset.mbFaqTab, true);
        },

        /** data-pressed on the [data-mb-press] element under a finger, until 250 ms after the finger lifts */
        onPress(event) {
            window.clearTimeout(pressTimer);
            if (event.type === 'touchstart') {
                pressed && pressed.removeAttribute('data-pressed');
                pressed = event.target.closest && event.target.closest('[data-mb-press]');
                pressed && pressed.setAttribute('data-pressed', '');
            } else if (pressed) {
                pressTimer = window.setTimeout(() => {
                    pressed.removeAttribute('data-pressed');
                    pressed = null;
                }, 250);
            }
        },

        // ---- collection tabs and scroll-spy (base and size pages) -------------------------------------------

        scrollToSection(tab) {
            const section = document.getElementById('col-' + tab.dataset.mbTab);
            if (!section) {
                return;
            }
            this.setTab(tab.dataset.mbTab);
            spyLock = Date.now() + 1500;
            section.scrollIntoView({behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start'});
            const heading = section.querySelector('h2');
            if (heading) {
                heading.tabIndex = -1;
                heading.focus({preventScroll: true});
            }
        },

        spy() {
            spyFrame = 0;
            const region = this.region();
            const nav = region && region.querySelector('[data-mb-nav]');
            nav && this.pin(nav);
            this.updateNavigationLinks();
            if (!nav || facts.page.kind === 'collection' || Date.now() < spyLock) {
                return;
            }
            const line = nav.getBoundingClientRect().bottom + 24;
            const sections = Array.from(region.querySelectorAll('section[data-collection]:not([hidden])'));
            const passed = sections.filter((section) => section.getBoundingClientRect().top <= line);
            const current = passed.length ? passed[passed.length - 1] : sections[0];
            current && this.setTab(current.dataset.collection);
        },

        /** [data-header-docked] on the sticky collection bar while it is pinned under the header (its sticky top) */
        pin(nav) {
            const top = parseFloat(getComputedStyle(nav).top) || 0;
            nav.toggleAttribute('data-header-docked', nav.getBoundingClientRect().top <= top + 0.5);
        },

        setTab(key) {
            const region = this.region();
            region.querySelectorAll('a[data-mb-tab]').forEach((tab) => {
                const current = tab.dataset.mbTab === key;
                const changed = current && tab.getAttribute('aria-current') !== 'true';
                this.mark(tab, current ? 'true' : null);
                if (current) {
                    const name = region.querySelector('[data-mb-tab-name]');
                    const who = region.querySelector('[data-mb-tab-who]');
                    if (name && name.textContent !== tab.dataset.mbLabel) name.textContent = tab.dataset.mbLabel;
                    if (who && who.textContent !== tab.dataset.mbWho) who.textContent = tab.dataset.mbWho;
                    if (changed) this.centre(tab);
                }
            });
        },

        /** Scrolls the element's sideways strip ([data-mb-strip], when it overflows) so the element is in the middle. */
        centre(element) {
            const strip = element.closest('[data-mb-strip]');
            if (strip && strip.scrollWidth > strip.clientWidth) {
                const box = element.getBoundingClientRect();
                const frame = strip.getBoundingClientRect();
                strip.scrollLeft += box.left + box.width / 2 - (frame.left + frame.width / 2);
            }
        },

        // ---- sheets (phones and tablets) -----------------------------------------------------------------

        openSheet(name, opener) {
            const region = this.region();
            const dialog = region.querySelector('[data-mb-sheet="' + CSS.escape(name) + '"]');
            const backdrop = region.querySelector('[data-mb-backdrop]');
            if (!dialog) {
                return;
            }
            this.closeSheet(false);
            sheet = name;
            sheetOpener = opener;
            dialog.hidden = false;
            backdrop && (backdrop.hidden = false);
            opener.setAttribute('aria-expanded', 'true');
            // the rest of the page is inert while the dialog is open: every sibling on the way up to <body>
            for (let element = dialog; element.parentElement && element !== document.body; element = element.parentElement) {
                Array.from(element.parentElement.children).forEach((sibling) => {
                    if (sibling !== element && sibling !== backdrop && !sibling.inert && sibling.tagName !== 'SCRIPT') {
                        sibling.inert = true;
                        inert.push(sibling);
                    }
                });
            }
            // on <body>, not <html>: its overflow then still applies to the viewport, so the body never becomes a
            // scroll container of its own and the collection bar stays pinned behind the backdrop
            document.body.classList.add('overflow-hidden');
            this.applyBar(region, Boolean(types.length || door), this.count());
            (dialog.querySelector('nav [aria-current]') || dialog.querySelector('nav a') || dialog).focus();
        },

        /** Closes the open sheet; always releases the background and the scroll lock. */
        closeSheet(returnFocus) {
            inert.forEach((element) => { element.inert = false; });
            inert = [];
            document.body.classList.remove('overflow-hidden');
            if (!sheet) {
                return;
            }
            const region = this.region();
            region.querySelectorAll('[data-mb-sheet], [data-mb-backdrop]').forEach((element) => {
                element.hidden = true;
                element.style.removeProperty('transform');
                element.style.removeProperty('transition');
            });
            region.querySelectorAll('[data-mb-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
            sheet = null;
            this.applyBar(region, Boolean(types.length || door), this.count());
            if (returnFocus && sheetOpener && sheetOpener.isConnected) {
                sheetOpener.focus({preventScroll: true});
            }
            sheetOpener = null;
        },

        /** Drag a sheet down by its handle: far or fast enough closes it, otherwise it springs back. */
        onPointerDown(event) {
            const handle = event.target.closest('[data-mb-drag]');
            const dialog = handle && handle.closest('[data-mb-sheet]');
            if (!dialog || event.button > 0 || event.target.closest('button')) {
                return;
            }
            const start = event.clientY;
            const time = performance.now();
            let distance = 0;
            const move = (moved) => {
                distance = Math.max(0, moved.clientY - start);
                dialog.style.transition = 'none';
                dialog.style.transform = 'translateY(' + distance + 'px)';
            };
            const end = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', end);
                window.removeEventListener('pointercancel', end);
                dialog.style.removeProperty('transition');
                if (distance > 80 || (distance > 20 && distance / Math.max(1, performance.now() - time) > 0.5)) {
                    this.closeSheet(true);
                } else {
                    dialog.style.removeProperty('transform');
                }
            };
            listen(window, 'pointermove', move);
            listen(window, 'pointerup', end);
            listen(window, 'pointercancel', end);
        },

        // ---- FAQ tabs, hashes, promo, bars -----------------------------------------------------------------

        setUpFaq() {
            const region = this.region();
            const selected = region.querySelector('[data-mb-faq-tab][aria-selected="true"]');
            const list = region.querySelector('#mb-faq-list');
            if (selected && list) {
                list.setAttribute('role', 'tabpanel');
                this.selectFaq(selected.dataset.mbFaqTab, false);
            }
        },

        selectFaq(group, focus) {
            const region = this.region();
            const list = region.querySelector('#mb-faq-list');
            region.querySelectorAll('[data-mb-faq-tab]').forEach((tab) => {
                const selected = tab.dataset.mbFaqTab === group;
                tab.setAttribute('aria-selected', String(selected));
                tab.tabIndex = selected ? 0 : -1;
                if (selected) {
                    list.setAttribute('aria-labelledby', tab.id);
                    focus && tab.focus();
                }
            });
            if (region.querySelector('[data-mb-faq-tab]')) {
                region.querySelectorAll('details[data-mb-faq-group]').forEach((card) => {
                    card.hidden = card.dataset.mbFaqGroup !== group;
                });
            }
        },

        /** Shows the target of a URL hash in the region (an FAQ card in another tab, a section) and scrolls to it. */
        reveal(hash) {
            let target = null;
            try {
                target = hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
            } catch (error) {
                target = null;
            }
            if (!target || !this.region().contains(target)) {
                return false;
            }
            if (target.matches('details[data-mb-faq-group]')) {
                this.selectFaq(target.dataset.mbFaqGroup, false);
                target.open = true;
            }
            target.scrollIntoView({block: 'start'});

            return true;
        },

        /**
         * The promo strip (magento-promo README "Murphy Beds strip"): a dismissed one stays hidden, every other one
         * gets its dismiss button. On a full load page.phtml's inline script has already hidden a dismissed strip,
         * before the first paint.
         */
        setUpPromo() {
            this.region().querySelectorAll('[data-promo-strip]').forEach((strip) => {
                let dismissed = false;
                try {
                    dismissed = window.sessionStorage.getItem(PROMO_DISMISSED_KEY + strip.dataset.promoStrip) !== null;
                } catch (error) {
                    dismissed = false;
                }
                strip.hidden = dismissed;
                const button = strip.querySelector('[data-promo-dismiss]');
                button && (button.hidden = dismissed);
            });
        },

        dismissPromo(button) {
            const strip = button.closest('[data-promo-strip]');
            if (!strip) {
                return;
            }
            const focused = strip.contains(document.activeElement);
            strip.hidden = true;
            try {
                window.sessionStorage.setItem(PROMO_DISMISSED_KEY + strip.dataset.promoStrip, '1');
            } catch (error) {
                // without storage the strip shows again on the next page
            }
            focused && this.focusHeading();
        },

        focusHeading() {
            const heading = this.region().querySelector('[data-mb-h1]');
            heading && heading.focus({preventScroll: true});
        },

        /** The H1 when it is on screen, else the first section heading that is (it takes tabindex -1), else the H1 */
        focusVisibleHeading() {
            const region = this.region();
            const offset = cssPx('--breda-header-h');
            const onScreen = (element) => {
                const box = element.getBoundingClientRect();
                return box.bottom > offset && box.top < window.innerHeight;
            };
            const heading = region.querySelector('[data-mb-h1]');
            const section = heading && onScreen(heading) ? null
                : Array.from(region.querySelectorAll('section[data-collection]:not([hidden]) h2')).find(onScreen);
            if (section) {
                section.setAttribute('tabindex', '-1');
                section.focus({preventScroll: true});
            } else {
                this.focusHeading();
            }
        },

        /**
         * After a filter change (R03): when no result is on screen any more (the document shortened under the
         * visitor), the first section shown or the empty state comes into view; results on screen stay put
         */
        keepResultsInView() {
            const top = cssPx('--breda-header-h') + cssPx('--mb-nav-h');
            const bottom = window.innerHeight - (desktop.matches ? 0 : cssPx('--mb-bar-h'));
            const shown = Array.from(this.region().querySelectorAll(
                'section[data-collection]:not([hidden]) li[data-mb-bed]:not([hidden]), [data-mb-empty]:not([hidden])'
            ));
            const inView = shown.some((element) => {
                const box = element.getBoundingClientRect();
                return box.bottom > top && box.top < bottom;
            });
            if (shown.length && !inView) {
                const target = shown[0].closest('section') || shown[0];
                target.scrollIntoView({block: 'start', behavior: reducedMotion.matches ? 'auto' : 'smooth'});
            }
        },

        /** Skip to filters (R11): the first control of the filters on screen, the desktop column's or the bar's */
        focusFilters() {
            const filters = this.region().querySelector(desktop.matches ? '[data-slot="column"]' : '[data-slot="bar"]');
            const first = filters && Array.from(filters.querySelectorAll('a[href]:not([data-mb-skip]), button, input'))
                .find((element) => element.checkVisibility());
            first && first.focus();
        },

        /** Skip to results, and after an empty state's Clear: the first product shown, else the empty state's action */
        focusResults() {
            const region = this.region();
            const first = region.querySelector(
                'section[data-collection]:not([hidden]) li[data-mb-bed]:not([hidden]) .product-item-link'
            ) || region.querySelector('[data-mb-empty]:not([hidden]) button:not([hidden])');
            first && first.focus();
        },

        /** Tab and Shift+Tab cycle inside the open sheet (R05): past its last control to its first and back */
        trapTab(event) {
            const dialog = this.region().querySelector('[data-mb-sheet="' + sheet + '"]');
            const selector = 'a[href], button, input, [tabindex]:not([tabindex="-1"])';
            const controls = dialog
                ? Array.from(dialog.querySelectorAll(selector)).filter((element) => element.checkVisibility()) : [];
            if (controls.length === 0) {
                return;
            }
            const first = controls[0];
            const last = controls[controls.length - 1];
            const active = document.activeElement;
            const outside = !controls.includes(active);
            if (event.shiftKey && (active === first || outside)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (active === last || outside)) {
                event.preventDefault();
                first.focus();
            }
        },

        /** --mb-nav-h and --mb-bar-h: the sticky collection bar's and the fixed phone bar's heights (0 when hidden) */
        observeBars() {
            const region = this.region();
            const nav = region.querySelector('[data-mb-nav]');
            const bar = region.querySelector('[data-mb-bar]');
            const measure = (entries) => {
                // ResizeObserver already measured the border boxes. Reading offsetHeight here forces
                // another layout after the header's observer changes inherited CSS properties.
                const heights = entries.map((entry) => [entry.target === nav ? '--mb-nav-h' : '--mb-bar-h',
                    Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight)]);
                const style = document.documentElement.style;
                const computed = getComputedStyle(document.documentElement);
                const changed = heights.filter(([name, height]) => computed.getPropertyValue(name).trim() !== height + 'px');
                changed.forEach(([name, height]) => {
                    const value = height + 'px';
                    if (style.getPropertyValue(name) !== value) style.setProperty(name, value);
                });
            };
            resizer && resizer.disconnect();
            resizer = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
            [nav, bar].forEach((element) => element && resizer && resizer.observe(element));
        },

        /** Real hrefs are ready for click, keyboard, new-tab and native speculation. */
        updateNavigationLinks() {
            const region = this.region();
            const scrolled = region.querySelector('#murphy-beds-results').getBoundingClientRect().top
                < cssPx('--breda-header-h');
            const sections = Array.from(region.querySelectorAll('section[data-collection]:not([hidden])'));
            const line = cssPx('--breda-header-h') + cssPx('--mb-nav-h') + 24;
            const passed = sections.filter((section) => section.getBoundingClientRect().top <= line);
            const current = passed.length ? passed[passed.length - 1] : sections[0];
            region.querySelectorAll('a[data-mb-size]').forEach((link) => {
                let hash = '';
                if (link.closest('[data-mb-sheet]')) hash = '#murphy-beds-results';
                else if (scrolled) {
                    const scope = facts.filters.sizes[link.dataset.mbSize || 'all'];
                    const matches = scope && filters.matcher(facts, {types, door, space}, link.dataset.mbSize || null, scope);
                    const members = current && facts.sections[current.dataset.collection];
                    const available = members && scope && members.some((id) => scope.beds.includes(id) && matches(id));
                    hash = available ? '#' + current.id : '#murphy-beds-results';
                }
                const href = this.compose(link.href, false) + hash;
                if (link.getAttribute('href') !== href) link.setAttribute('href', href);
            });
            if (facts.page.kind === 'collection') {
                region.querySelectorAll('a[data-mb-tab]').forEach((link) => {
                    const href = this.compose(link.href, false) + (scrolled ? '#murphy-beds-results' : '');
                    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
                });
            }
        },

        onPopState(event) {
            if (this.keyOf(new URL(location.href)) !== facts.page.key) return;
            const next = this.filtersOf(new URL(location.href));
            const state = this.ownState(event.state);
            types = next.types;
            door = next.door;
            space = next.space;
            more = state ? Object.assign({}, state.more) : {};
            this.closeSheet(false);
            this.apply();
            this.spy();
        }

    };
}
